import React from "react";
import MUIDataTable from "mui-datatables";
import { ThemeProvider } from "@mui/material/styles";
import { Box, CircularProgress, InputBase, Typography } from "@mui/material";
import { MuiTheme, textLabels } from "../../styles/MuiOverrideStyles";
import { tokens } from "../../theme";
import { IconClose, IconSearch } from "./icons";
import DateRangeFilter from "./DateRangeFilter";

/**
 * Listado único del sistema.
 *
 * Toda pantalla con una lista usa este componente, así la barra se ve y se usa
 * igual en órdenes, clientes, facturación, caja y reportes: buscador a la
 * izquierda, filtros al lado, y a la derecha el grupo de acciones de siempre
 * (descargar, imprimir, columnas, filtros avanzados).
 */

const chipStyle = (active) => ({
  display: "flex",
  alignItems: "center",
  gap: "7px",
  minHeight: { xs: 40, md: 34 },
  padding: "0 12px",
  borderRadius: 999,
  fontSize: "13px",
  whiteSpace: "nowrap",
  cursor: "pointer",
  flexShrink: 0,
  fontWeight: active ? 500 : 400,
  border: `1px solid ${active ? tokens.graphite : tokens.border}`,
  backgroundColor: active ? tokens.graphite : tokens.surface,
  color: active ? "#fff" : tokens.ink2,
});

/** La barra viaja como `title` de la tabla: queda en la misma fila que los íconos. */
const ListToolbar = ({
  heading,
  search,
  dateRange,
  chips,
  filters,
  actions,
  resultCount,
  totalCount,
}) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", width: "100%", paddingY: "8px" }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
      {heading && (
        <Typography sx={{ fontSize: "14px", fontWeight: 600, marginRight: "4px" }}>
          {heading}
        </Typography>
      )}

      {search && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "9px",
            flexGrow: 1,
            minWidth: { xs: "100%", sm: 240 },
            maxWidth: { sm: 380 },
            minHeight: { xs: 44, md: 36 },
            padding: "0 13px",
            border: `1px solid ${tokens.border}`,
            borderRadius: `${tokens.radiusControl}px`,
            backgroundColor: tokens.surface,
            "&:focus-within": {
              borderColor: tokens.brand,
              boxShadow: `0 0 0 3px ${tokens.brandTint}`,
            },
          }}
        >
          <Box sx={{ color: tokens.ink3, display: "flex" }}>
            <IconSearch size={16} />
          </Box>
          <InputBase
            value={search.value}
            onChange={(e) => search.onChange(e.target.value)}
            placeholder={search.placeholder || "Buscar…"}
            sx={{ flexGrow: 1, fontSize: { xs: "15px", md: "13.5px" } }}
            inputProps={{ "aria-label": search.placeholder || "Buscar" }}
          />
          {search.value ? (
            <Box
              onClick={() => search.onChange("")}
              aria-label="Borrar búsqueda"
              sx={{ display: "flex", color: tokens.ink3, cursor: "pointer" }}
            >
              <IconClose size={15} />
            </Box>
          ) : null}
        </Box>
      )}

      {dateRange && (
        <DateRangeFilter
          from={dateRange.from}
          to={dateRange.to}
          onChange={dateRange.onChange}
          label={dateRange.label}
        />
      )}

      {filters}

      <Box sx={{ display: "flex", alignItems: "center", gap: "10px", marginLeft: "auto" }}>
        {typeof resultCount === "number" && (
          <Typography
            sx={{ fontSize: "12.5px", color: tokens.ink3, display: { xs: "none", md: "block" } }}
          >
            {resultCount === totalCount
              ? `${totalCount} ${totalCount === 1 ? "registro" : "registros"}`
              : `${resultCount} de ${totalCount}`}
          </Typography>
        )}
        {/* La acción principal de la pantalla vive en la misma barra que los filtros. */}
        {actions}
      </Box>
    </Box>

    {chips && chips.length > 0 && (
      <Box
        sx={{
          display: "flex",
          gap: "7px",
          overflowX: "auto",
          paddingBottom: "2px",
          "&::-webkit-scrollbar": { display: "none" },
          scrollbarWidth: "none",
        }}
      >
        {chips.map((chip) => (
          <Box key={chip.id ?? chip.label} onClick={chip.onClick} sx={chipStyle(chip.active)}>
            {chip.dot && (
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: 999,
                  flexShrink: 0,
                  backgroundColor: chip.dot,
                }}
              />
            )}
            {chip.label}
          </Box>
        ))}
      </Box>
    )}
  </Box>
);

const DataTable = ({
  data,
  columns,
  loading = false,
  heading,
  search,
  dateRange,
  chips,
  filters,
  actions,
  resultCount,
  totalCount,
  emptyMessage = "No se encontraron registros",
  options = {},
}) => {
  // MUI avisa si el valor de "filas por página" no está entre las opciones.
  // Se arma la lista a partir del valor efectivo para que nunca se desalineen.
  const effectiveRowsPerPage = options.rowsPerPage ?? 15;
  const baseRowsPerPageOptions = options.rowsPerPageOptions ?? [15, 30, 50, 100];
  const rowsPerPageOptions = [...new Set([...baseRowsPerPageOptions, effectiveRowsPerPage])].sort(
    (a, b) => a - b
  );

  const mergedOptions = {
    // La búsqueda es nuestra; el resto de las acciones son las de la librería,
    // que ya saben exportar, imprimir, ocultar columnas y filtrar.
    search: false,
    download: true,
    print: true,
    viewColumns: true,
    filter: true,
    filterType: "dropdown",
    responsive: "standard",
    selectableRows: "none",
    elevation: 0,
    rowsPerPageOptions,
    downloadOptions: {
      filename: "listado.csv",
      separator: ";",
      filterOptions: { useDisplayedColumnsOnly: true, useDisplayedRowsOnly: true },
    },
    textLabels: {
      ...textLabels,
      body: { ...textLabels.body, noMatch: emptyMessage },
      filter: { ...textLabels.filter, all: "Todos" },
    },
    ...options,
    rowsPerPage: effectiveRowsPerPage,
    rowsPerPageOptions,
  };

  return (
    <ThemeProvider theme={MuiTheme}>
      <Box
        sx={{
          position: "relative",
          width: "100%",
          opacity: loading ? 0.55 : 1,
          transition: "opacity 120ms ease",
        }}
      >
        {loading && (
          <Box sx={{ position: "absolute", top: 16, right: 18, zIndex: 10 }}>
            <CircularProgress size={20} />
          </Box>
        )}

        <MUIDataTable
          title={
            <ListToolbar
              heading={heading}
              search={search}
              dateRange={dateRange}
              chips={chips}
              filters={filters}
              actions={actions}
              resultCount={resultCount}
              totalCount={totalCount}
            />
          }
          data={data}
          columns={columns}
          options={mergedOptions}
        />
      </Box>
    </ThemeProvider>
  );
};

export default DataTable;
